import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  Lock,
} from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { cn } from '../../lib/cn';
import { verifyTotp, useMfaPrimaryFactor } from '../../api/platform';

const TOTP_LENGTH = 6;
const MAX_ATTEMPTS = 5;

/**
 * 2FA challenge — shown after password login when the user has 2FA enabled.
 * Mock mode: code "123456" succeeds; recovery code accepts any non-empty value.
 */
export function MfaChallenge() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { data: primaryFactor } = useMfaPrimaryFactor();

  const [digits, setDigits] = useState<string[]>(
    Array.from({ length: TOTP_LENGTH }, () => ''),
  );
  const inputs = useRef<Array<HTMLInputElement | null>>(
    Array.from({ length: TOTP_LENGTH }, () => null),
  );

  const [status, setStatus] = useState<
    'idle' | 'verifying' | 'success' | 'failed' | 'locked'
  >('idle');
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);

  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  // Focus first cell on mount
  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  function setDigit(i: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const copy = [...prev];
      copy[i] = digit;
      return copy;
    });
    if (digit && i < TOTP_LENGTH - 1) {
      inputs.current[i + 1]?.focus();
    }
    if (status === 'failed') setStatus('idle');
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      inputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && i < TOTP_LENGTH - 1) {
      inputs.current[i + 1]?.focus();
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, TOTP_LENGTH);
    if (text.length === 0) return;
    e.preventDefault();
    const next = Array.from({ length: TOTP_LENGTH }, () => '');
    for (let i = 0; i < text.length && i < TOTP_LENGTH; i++) {
      next[i] = text[i]!;
    }
    setDigits(next);
    const focusIndex = Math.min(text.length, TOTP_LENGTH - 1);
    inputs.current[focusIndex]?.focus();
    if (status === 'failed') setStatus('idle');
  }

  const code = digits.join('');
  const allFilled = code.length === TOTP_LENGTH && /^\d{6}$/.test(code);

  function verify(e?: React.FormEvent) {
    e?.preventDefault();
    if (!allFilled || status === 'verifying' || status === 'locked') return;
    setStatus('verifying');
    verifyTotp(code).then((ok) => {
      if (ok) {
        setStatus('success');
        setTimeout(() => navigate({ to: '/' }), 400);
      } else {
        const next = attemptsLeft - 1;
        setAttemptsLeft(next);
        setDigits(Array.from({ length: TOTP_LENGTH }, () => ''));
        if (next <= 0) {
          setStatus('locked');
        } else {
          setStatus('failed');
          setTimeout(() => inputs.current[0]?.focus(), 0);
        }
      }
    });
  }

  function submitRecovery(e: React.FormEvent) {
    e.preventDefault();
    setRecoveryError(null);
    if (!recoveryCode.trim()) {
      setRecoveryError('Enter your recovery code.');
      return;
    }
    setRecoverySubmitting(true);
    setTimeout(() => {
      setRecoverySubmitting(false);
      setStatus('success');
      setTimeout(() => navigate({ to: '/' }), 400);
    }, 800);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </div>
        <AuthThemeToggle />
      </header>

      <main className="px-4 sm:px-6 py-10 sm:py-16 max-w-md mx-auto">
        <div className="text-center mb-6">
          <span className="inline-flex w-14 h-14 grid place-items-center rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 mb-4">
            <Lock className="w-7 h-7" />
          </span>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Two-factor authentication
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            Enter the 6-digit code from your authenticator app to continue.
          </p>
          <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            Using:{' '}
            <span className="font-medium text-zinc-700 dark:text-zinc-200">
              {primaryFactor?.label}
            </span>
          </p>
        </div>

        <Card className="p-5 sm:p-7">
          {status === 'locked' ? (
            <div className="space-y-4">
              <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-sm text-rose-900 dark:text-rose-100">
                  <AlertCircle className="w-4 h-4" />
                  Too many attempts. Sign in again from{' '}
                  <span className="mono">/login</span>.
                </div>
              </div>
              <Link
                to="/login"
                className="block w-full text-center px-4 py-2.5 rounded-md text-sm font-medium bg-brand text-white hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
              >
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={verify} className="space-y-5">
              <div className="flex items-center justify-center gap-2 sm:gap-2.5">
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={d}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    onPaste={onPaste}
                    disabled={status === 'verifying' || status === 'success'}
                    aria-label={`2FA digit ${i + 1}`}
                    className={cn(
                      'w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-semibold tabular-nums',
                      'bg-white dark:bg-zinc-900',
                      'border-2 rounded-lg',
                      'text-zinc-950 dark:text-zinc-50',
                      'focus:outline-none focus:ring-2',
                      status === 'failed'
                        ? 'border-rose-400 dark:border-rose-700 focus:ring-rose-500'
                        : status === 'success'
                        ? 'border-emerald-400 dark:border-emerald-700 focus:ring-emerald-500'
                        : 'border-zinc-300 dark:border-zinc-700 focus:ring-brand dark:focus:ring-brand-dm focus:border-brand dark:focus:border-brand-dm',
                      (status === 'verifying' || status === 'success') &&
                        'opacity-80 cursor-not-allowed',
                    )}
                  />
                ))}
              </div>

              <p className="text-center text-[11px] text-zinc-500 dark:text-zinc-400">
                (mock: use 123456)
              </p>

              {status === 'verifying' && (
                <div className="flex items-center justify-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
                  <Loader2 className="w-4 h-4 animate-spin text-brand dark:text-brand-dm" />
                  Verifying…
                </div>
              )}

              {status === 'success' && (
                <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  Verified — taking you in…
                </div>
              )}

              {status === 'failed' && (
                <div className="flex items-center justify-center gap-2 text-sm text-rose-600 dark:text-rose-400">
                  <AlertCircle className="w-4 h-4" />
                  Code didn't match. {attemptsLeft}{' '}
                  {attemptsLeft === 1 ? 'attempt' : 'attempts'} left.
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to login
                </Link>
                <Button
                  type="submit"
                  loading={status === 'verifying'}
                  disabled={!allFilled || status === 'success'}
                >
                  Verify
                </Button>
              </div>

              <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
                <button
                  type="button"
                  onClick={() => setRecoveryOpen((o) => !o)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-brand dark:text-brand-dm hover:underline"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  Lost your device? Use a recovery code
                  <span className="opacity-60">→</span>
                </button>

                {recoveryOpen && (
                  <div className="mt-3 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-800/40 p-3 sm:p-4 space-y-3">
                    <p className="text-xs text-zinc-600 dark:text-zinc-300">
                      Enter one of the 12-character recovery codes you saved when
                      you set up 2FA. Each code works once.
                    </p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={recoveryCode}
                        onChange={(e) => {
                          setRecoveryCode(e.target.value);
                          if (recoveryError) setRecoveryError(null);
                        }}
                        placeholder="xxxx-xxxx-xxxx"
                        maxLength={14}
                        className={cn(
                          'w-full px-3 py-2.5 rounded-md mono text-sm tracking-wider',
                          'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                          'border border-zinc-300 dark:border-zinc-700',
                          'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
                        )}
                        autoComplete="off"
                      />
                      {recoveryError && (
                        <p className="text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {recoveryError}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setRecoveryOpen(false);
                          setRecoveryCode('');
                          setRecoveryError(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        loading={recoverySubmitting}
                        onClick={submitRecovery}
                      >
                        Use recovery code
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </Card>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Lock className="w-3 h-3" />
          Encrypted · Codes refresh every 30 seconds
        </p>
      </main>
    </div>
  );
}
